import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  Alert,
  Tooltip,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import {
  REPORT_TYPE_LABELS,
  REPORT_TYPES,
  GROUPING_LABELS,
  GROUPING,
  METODO_PAGO,
  reportUsesProductFilter,
  reportUsesMetodoPago,
} from '../utils/reportesData';
import { filtersDateRangeInvalid } from '../../utils/inventarioFilters';
import ProductoFilterAutocomplete from '../../components/ProductoFilterAutocomplete';

export default function ReportesFiltersBar({ filters, onChange, productos = [] }) {
  const invalidRange = filtersDateRangeInvalid(filters);
  const showProduct = reportUsesProductFilter(filters.reportType);
  const showMetodoPago = reportUsesMetodoPago(filters.reportType);

  const setField = (field, value) => {
    onChange({ ...filters, [field]: value });
  };

  return (
    <Box
      sx={{
        mb: 3,
        p: 2,
        bgcolor: 'grey.50',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', lg: 'flex-end' }}
        justifyContent="space-between"
      >
        <Typography
          variant="subtitle2"
          fontWeight={700}
          sx={{ display: { xs: 'none', lg: 'block' }, minWidth: 72, pb: 1 }}
        >
          Filtros
        </Typography>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flex={1} flexWrap="wrap" useFlexGap>
          <TextField
            label="Desde"
            type="date"
            size="small"
            value={filters.dateFrom}
            onChange={(e) => setField('dateFrom', e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />
          <TextField
            label="Hasta"
            type="date"
            size="small"
            value={filters.dateTo}
            onChange={(e) => setField('dateTo', e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Tipo de reporte</InputLabel>
            <Select
              value={filters.reportType}
              label="Tipo de reporte"
              onChange={(e) => setField('reportType', e.target.value)}
            >
              {Object.entries(REPORT_TYPE_LABELS).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Agrupación</InputLabel>
            <Select
              value={filters.agrupacion}
              label="Agrupación"
              onChange={(e) => setField('agrupacion', e.target.value)}
              disabled={filters.reportType === REPORT_TYPES.INVENTARIO}
            >
              {Object.entries(GROUPING_LABELS).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {showProduct && (
            <ProductoFilterAutocomplete
              productos={productos}
              value={filters.productId}
              onChange={(productId) => setField('productId', productId)}
              allLabel="Todos los materiales"
              label="Material"
              sx={{ minWidth: 220, maxWidth: 320 }}
            />
          )}

          {showMetodoPago && (
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Método pago</InputLabel>
              <Select
                value={filters.metodoPago}
                label="Método pago"
                onChange={(e) => setField('metodoPago', e.target.value)}
              >
                <MenuItem value={METODO_PAGO.ALL}>Todos</MenuItem>
                <MenuItem value={METODO_PAGO.EFECTIVO}>Efectivo</MenuItem>
                <MenuItem value={METODO_PAGO.QR}>QR</MenuItem>
              </Select>
            </FormControl>
          )}
        </Stack>

        <Tooltip title="Próximamente (PDF / Excel)">
          <span>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              disabled
              sx={{ whiteSpace: 'nowrap' }}
            >
              Exportar
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {invalidRange && (
        <Alert severity="error" sx={{ mt: 1.5, py: 0 }}>
          La fecha inicial no puede ser posterior a la final.
        </Alert>
      )}

      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
        Por defecto: día 1 del mes en curso hasta hoy. Los cambios actualizan KPIs, gráfico y tabla al instante.
      </Typography>
    </Box>
  );
}
